import { api } from "./api";

export interface CustomizationData {
  productId: string;
  customizationType: "2d-print" | "3d-print";
  elements?: any[];
  canvasSize?: string;
  backgroundColor?: string;
  backgroundImage?: string;
  previewImage?: string; // Preview image URL for 3D designs
  isDraft?: boolean;
}

export interface CustomizationResponse {
  _id: string;
  [key: string]: any;
}

export interface ImageResponse {
  imageUrl: string;
}

export interface PreviewResponse {
  previewUrl: string;
}

export const productCustomizationApi = {
  async create(data: CustomizationData): Promise<CustomizationResponse> {
    const response = await api.post("/product-customizations", data);
    return response.data;
  },

  async update(id: string, data: CustomizationData): Promise<CustomizationResponse> {
    const response = await api.put(`/product-customizations/${id}`, data);
    return response.data;
  },

  async getImage(customizationId: string): Promise<ImageResponse> {
    const response = await api.get(`/product-customizations/${customizationId}/image`);
    return response.data;
  },

  async exportImage(customizationId: string): Promise<Blob> {
    const response = await api.get(`/product-customizations/${customizationId}/export`, {
      responseType: "blob",
    });
    return response.data;
  },

  async getModelPreview(customizationId: string): Promise<PreviewResponse> {
    const response = await api.get(`/product-customizations/${customizationId}/model-preview`);
    return response.data;
  },

  async exportSTL(customizationId: string): Promise<Blob> {
    const response = await api.get(`/product-customizations/${customizationId}/export-stl`, {
      responseType: "blob",
    });
    return response.data;
  },

  async exportOBJ(customizationId: string): Promise<Blob> {
    const response = await api.get(`/product-customizations/${customizationId}/export-obj`, {
      responseType: "blob",
    });
    return response.data;
  },

  async getCustomizations(params?: {
    productId?: string;
    customizationType?: "2d-print" | "3d-print";
  }): Promise<CustomizationResponse[]> {
    const queryParams = new URLSearchParams();
    if (params?.productId) queryParams.append("productId", params.productId);
    if (params?.customizationType) queryParams.append("customizationType", params.customizationType);

    const response = await api.get(`/product-customizations?${queryParams.toString()}`);
    return response.data.customizations || [];
  },

  async getCustomizationById(id: string): Promise<CustomizationResponse> {
    const response = await api.get(`/product-customizations/${id}`);
    return response.data;
  },

  /**
   * Fetch an image from a URL and return it as a base64 data URL
   * This bypasses CORS by fetching on the backend
   */
  async fetchImageAsDataURL(imageUrl: string): Promise<string> {
    const response = await api.get("/product-customizations/images/fetch", {
      params: { url: imageUrl },
    });
    return response.data.dataURL;
  },
};
